import type { ReactNode } from "react";

import styled from "styled-components";

import { AdIntelMetricsUnavailableBanner } from "app/pages/digital-marketing/ad-intelligence/common/banners/AdIntelMetricsUnavailableBanner";

import type { IIndustryDataAvailabilityState } from "./industryDataAvailability";
import { INDUSTRY_DATA_AVAILABILITY_BANNER_KEY } from "./industryDataAvailability";

interface IIndustryDataAvailabilityBannerProps {
    availability: IIndustryDataAvailabilityState;
}

// Same visual as the Tracked Advertiser Activity notice (gray strip, bell icon,
// text) — only the copy differs per availability boundary. No info tooltip: the
// copy already states the boundary, so there is nothing left to explain.
// Always renders; callers decide whether a notice is needed via
// `renderIndustryDataAvailabilityNotice`.
export const IndustryDataAvailabilityBanner = ({ availability }: IIndustryDataAvailabilityBannerProps) => (
    <AdIntelMetricsUnavailableBanner
        text={INDUSTRY_DATA_AVAILABILITY_BANNER_KEY[availability.minDate]}
        tooltipText={null}
    />
);

/**
 * Notice for a `noticeSlot`-style prop, or `undefined` when the selected range is
 * fully supported.
 */
export const renderIndustryDataAvailabilityNotice = (availability: IIndustryDataAvailabilityState): ReactNode =>
    availability.isBeforeMinDate ? <IndustryDataAvailabilityBanner availability={availability} /> : undefined;

// Page-level banners sit above the content card, on the page background, with the
// same 24px gap `MixedScrapperBanner` uses — see `AdsGallery`, which renders that
// banner as a sibling above its card.
const PageBannerWrapper = styled.div`
    margin-bottom: 24px;
`;

/**
 * Availability notice for a full report page (Ads, Campaigns, Publishers).
 *
 * Render this as a sibling *above* the page's content card, never inside it —
 * page-level notices are page furniture, matching `MixedScrapperBanner`. Overview
 * widgets use `renderIndustryDataAvailabilityNotice` in a `noticeSlot` instead,
 * because there the notice belongs to the individual widget.
 */
export const IndustryDataAvailabilityPageBanner = ({ availability }: IIndustryDataAvailabilityBannerProps) =>
    availability.isBeforeMinDate ? (
        <PageBannerWrapper>
            <IndustryDataAvailabilityBanner availability={availability} />
        </PageBannerWrapper>
    ) : null;
